import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { dashboardApi } from '../api/dashboard.api'

interface DashboardFiltros {
  fecha_desde?: string
  fecha_hasta?: string
  farmacia?: string
  provincia?: string
  promotor?: string
}

export function useDashboardConfig(grupoId: string) {
  return useQuery({
    queryKey: ['dashboard-config', grupoId],
    queryFn: () => dashboardApi.getConfig(grupoId),
    enabled: !!grupoId,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always' as const,
    staleTime: 0,
  })
}

export function useDashboardEstadisticas(grupoId: string, filtros?: DashboardFiltros) {
  return useQuery({
    queryKey: ['dashboard-stats', grupoId, filtros],
    queryFn: () => dashboardApi.getEstadisticas(grupoId, filtros),
    enabled: !!grupoId,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always' as const,
    staleTime: 0,
    placeholderData: keepPreviousData,
  })
}

export function useDashboardGraficos(grupoId: string, configId?: number, filtros?: DashboardFiltros) {
  return useQuery({
    queryKey: ['dashboard-graficos', grupoId, configId, filtros],
    queryFn: () => dashboardApi.getGraficos(grupoId, configId, filtros),
    enabled: !!grupoId,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always' as const,
    staleTime: 0,
  })
}

export function useDashboardTabla(
  grupoId: string,
  configId: number,
  params?: {
    page?: number
    page_size?: number
    ordenar_por?: string
    busqueda?: string
  } & DashboardFiltros
) {
  return useQuery({
    queryKey: ['dashboard-tabla', grupoId, configId, params],
    queryFn: () => dashboardApi.getTablaDatos(grupoId, configId, params),
    enabled: !!grupoId && !!configId,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always' as const,
    staleTime: 0,
  })
}

export function useDashboardTablaCompleta(grupoId: string, configId: number) {
  return useQuery({
    queryKey: ['dashboard-tabla-completa', grupoId, configId],
    queryFn: () => dashboardApi.getTablaDatos(grupoId, configId, { todos: true }),
    enabled: !!grupoId && !!configId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

export function useDashboardFiltros(grupoId: string, configId?: number) {
  return useQuery({
    queryKey: ['dashboard-filtros', grupoId, configId ?? 'principal'],
    queryFn: () => dashboardApi.getFiltros(grupoId, configId),
    enabled: !!grupoId,
    staleTime: 60000,
  })
}

export function useDashboardUsuario(grupoId: string, userId: number) {
  return useQuery({
    queryKey: ['dashboard-usuario', grupoId, userId],
    queryFn: () => dashboardApi.getUsuarioDetalle(grupoId, userId),
    enabled: !!grupoId && !!userId,
    refetchOnWindowFocus: true,
    staleTime: 0,
  })
}

export function useDashboardComprobante(grupoId: string, comprobanteId: number | null) {
  return useQuery({
    queryKey: ['dashboard-comprobante', grupoId, comprobanteId],
    queryFn: () => dashboardApi.getComprobante(grupoId, comprobanteId!),
    enabled: !!grupoId && !!comprobanteId,
    staleTime: 5 * 60 * 1000,
  })
}

export function useDashboardBusqueda(
  grupoId: string,
  query: string,
  campo: string = 'nombre'
) {
  return useQuery({
    queryKey: ['dashboard-busqueda', grupoId, query, campo],
    queryFn: () => dashboardApi.buscar(grupoId, query, campo),
    enabled: !!grupoId && query.length >= 2,
    staleTime: 30000,
  })
}
